import React from 'react';

import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

import ListEmptyState from './ListEmptyState';

export type ListPanelItem = {
  id: string;
  primary: string;
  secondary?: string;
};

type ListPanelProps = {
  items: ListPanelItem[];
  selectedId?: string;
  onSelect: (item: ListPanelItem) => void;
  pageSize?: number;
  height?: string;
  renderItem?: (item: ListPanelItem, selected: boolean) => React.ReactNode;
  /** 件数表示の右側に配置するアクション要素 */
  headerAction?: React.ReactNode;
  /** ローディング中はスケルトン行を表示する（任意・後方互換） */
  loading?: boolean;
  /** データが0件のときに表示する空状態（任意・未指定時は既定の空状態） */
  emptyState?: React.ReactNode;
};

/** スケルトン行（読み込み中のリストプレースホルダ） */
const SkeletonRow: React.FC = () => (
  <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Skeleton variant="rounded" width={28} height={28} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="60%" height={18} />
        <Skeleton variant="text" width="40%" height={14} />
      </Box>
      <Skeleton variant="rounded" width={44} height={20} />
    </Stack>
  </Box>
);

const ListPanel: React.FC<ListPanelProps> = ({
  items,
  selectedId,
  onSelect,
  pageSize = 50,
  height = 'calc(100vh - 228px)',
  renderItem,
  headerAction,
  loading = false,
  emptyState,
}) => {
  const [page, setPage] = React.useState(1);

  const visibleItems = React.useMemo(
    () => [...items].slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [page, items, pageSize],
  );

  const pageCount = React.useMemo(() => {
    if (!items || items.length === 0) return 0;
    return Math.ceil(items.length / pageSize);
  }, [items, pageSize]);

  React.useEffect(() => {
    setPage(1);
  }, [items]);

  return (
    <Stack direction="column" gap={1}>
      {/* 件数表示 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
        {loading ? (
          <Skeleton variant="text" width={48} height={18} />
        ) : (
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {items.length} 件
          </Typography>
        )}
        {headerAction}
      </Box>
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <List
          sx={{
            width: '100%',
            position: 'relative',
            overflow: 'auto',
            height: pageCount > 1 ? height : 'calc(100vh - 192px)',
            py: 0,
          }}
        >
          {loading
            ? [0, 1, 2, 3, 4, 5, 6].map((i) => <SkeletonRow key={`skeleton-${i}`} />)
            : visibleItems.length === 0
              ? (emptyState ?? <ListEmptyState />)
              : visibleItems.map((item) => (
                  <React.Fragment key={`item-${item.id}`}>
                    {renderItem ? (
                      <ListItemButton
                        divider
                        selected={selectedId === item.id}
                        onClick={() => onSelect(item)}
                        sx={{
                          borderLeft: 3,
                          borderLeftColor: selectedId === item.id ? 'primary.main' : 'transparent',
                          transition: 'all 0.15s ease',
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                          },
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        {renderItem(item, selectedId === item.id)}
                      </ListItemButton>
                    ) : (
                      <ListItemButton
                        divider
                        selected={selectedId === item.id}
                        onClick={() => onSelect(item)}
                        sx={{
                          borderLeft: 3,
                          borderLeftColor: selectedId === item.id ? 'primary.main' : 'transparent',
                          transition: 'all 0.15s ease',
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                          },
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemText
                          primary={item.primary}
                          secondary={item.secondary}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        />
                      </ListItemButton>
                    )}
                  </React.Fragment>
                ))}
        </List>
      </Paper>
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
          <Pagination
            size="small"
            page={page}
            onChange={(_, p) => setPage(p)}
            count={pageCount}
            shape="rounded"
          />
        </Box>
      )}
    </Stack>
  );
};

export default ListPanel;
